import { defineComponent as _defineComponent } from 'vue'
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import {
  computed, PropType, ref, toRefs, Ref
} from 'vue';
import { BcxFormField } from '@/models/BcxForm';
import { asyncComputed, whenever } from '@vueuse/core';
import ConfirmButtons from '@/components/molecules/ConfirmButtons.vue';
import { SelectItem } from '@/models/SelectItem';
import { getPlatformCountries } from '@/services/Geo/Api';
import useI18n from '@/mixins/useI18n';
import BCXButton from '@/components/molecules/BCXButton.vue';
import useBcxForm from '@/mixins/useBcxForm';
import { useStore } from 'vue2-helpers/vuex';
import { City } from '@/models/Geo';

interface Address {
  countryIsoCode: string;
  street: string;
  houseNumber: string;
  cityAndZip: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsContractBaseAddress',
  props: {
  label: {
    type: String,
    required: true
  },
  displayName: {
    type: String,
    default: '',
    required: true
  },
  data: {
    type: Object as PropType<Record<string, string>>,
    required: true
  },
  isEditing: Boolean,
  isPartner: Boolean
},
  emits: ['update:data', 'update:isEditing', 'update:abort'],
  setup(__props, { emit }) {

const props = __props;



const { data } = toRefs(props);



const tmpData: Ref<Address> = ref({
  countryIsoCode: '',
  street: '',
  houseNumber: '',
  cityAndZip: ''
});

const { t } = useI18n();

const hasData = computed(() => Object.entries(props.data)
  .filter(([key]) => key !== 'name')
  .some(([, value]) => value));

const onAbort = () => {
  emit('update:isEditing', false);
  emit('update:abort');
};

const countries = asyncComputed<SelectItem[]>(
  async () => {
    const countries = await getPlatformCountries();
    return [
      {
        value: '',
        text: `${t('common.choose')}`
      },
      ...countries.map(({
        countryName,
        isoCode
      }) => ({
        value: isoCode,
        text: countryName
      }))
    ];
  },
  [],
  { lazy: true }
);

const store = useStore();

const cities = asyncComputed<SelectItem[]>(
  async () => {
    const { cities: citiesList }: { cities: City[] } = store.state.projectForum;
    return citiesList.map(({
      placeName,
      postalCode
    }) => {
      const formattedPlaceName = `${postalCode}, ${placeName.split(',')[0]?.trim() ?? placeName}`;
      return {
        value: formattedPlaceName,
        text: formattedPlaceName
      };
    });
  },
  [],
  { lazy: true }
);

const addressFields = computed<BcxFormField[]>(() => [
  {
    type: 'select',
    label: `${t('contracts.contract.base-data.address.country')}`,
    name: 'countryIsoCode',
    width: '100%',
    fieldWidth: '80%',
    required: true,
    options: countries.value,
  },
  {
    type: 'select',
    label: `${t('contracts.contract.base-data.address.city')}`,
    name: 'cityAndZip',
    required: true,
    width: '100%',
    fieldWidth: '80%',
    mode: 'input',
    options: cities.value,
    placeholder: t('contracts.contract.base-data.address.placeholder').toString(),
    disabled: !tmpData.value.countryIsoCode
  },
  {
    type: 'text',
    label: `${t('contracts.contract.base-data.address.street')}`,
    name: 'street',
    required: true,
    width: 'calc(100% - 80px)',
    textOptions: {
      maxLength: 40,
      hideCharsLeft: true
    }
  },
  {
    type: 'text',
    label: `${t('contracts.contract.base-data.address.nr')}`,
    name: 'houseNumber',
    required: true,
    width: '80px',
    textOptions: {
      maxLength: 5,
      hideCharsLeft: true
    }
  },
]);

const {
  errorMessages,
  onBlur,
  hasErrors,
  validateAllFields
} = useBcxForm(tmpData as unknown as Ref<Record<string, string>>, addressFields);

const onFieldBlur = (field: BcxFormField) => {
  onBlur(field);
};

const updateData = async (updatedData: Address) => {
  if (!updatedData.countryIsoCode || !updatedData.cityAndZip) {
    return;
  }

  await store.dispatch('projectForum/fetchCities', { countryId: updatedData.countryIsoCode, searchString: updatedData.cityAndZip });
};

const onSave = () => {
  if (!tmpData.value.cityAndZip.includes(', ')) {
    // Makes sure users have selected a value from the list (or at least are following the same format)
    // Without this ZIP only or city only would be valid
    // TODO: This is a bit of a hack, is there a way to do this in the validation? (MB)
    tmpData.value.cityAndZip = '';
  }

  validateAllFields();
  if (hasErrors.value) {
    return;
  }

  const [postalCode, city] = tmpData.value.cityAndZip.split(', ');

  const updatedData = {
    countryIsoCode: tmpData.value.countryIsoCode,
    street: tmpData.value.street,
    houseNumber: tmpData.value.houseNumber,
    city: city ?? '',
    postalCode: postalCode ?? ''
  };

  emit('update:data', updatedData);
  emit('update:isEditing', false);
};

whenever(
  () => props.isEditing,
  () => {
    const data = { ...props.data };

    let cityAndZip = '';

    if (data.postalCode) {
      cityAndZip = `${data.postalCode}, `;
    }

    if (data.city) {
      cityAndZip += `${data.city}`;
    }

    tmpData.value = {
      countryIsoCode: data.countryIsoCode,
      street: data.street,
      houseNumber: data.houseNumber,
      cityAndZip
    };
  }
);


return { __sfc: true,props, data, emit, tmpData, t, hasData, onAbort, countries, store, cities, addressFields, errorMessages, onBlur, hasErrors, validateAllFields, onFieldBlur, updateData, onSave, BCXForm, ConfirmButtons, BCXButton }
}

})